import { cva } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';

import { getChannelIconName } from '@/utils/channel-icon';
import { cn } from '@/utils/classnames';

import { LetterAvatar } from './letter-avatar';
import { Tooltip } from './tooltip';
import { type TypographyVariant, Typography } from './typography';
import { Skeleton } from './skeleton';

type IconSize = 'xs' | 'sm' | 'md' | 'lg';

/** Maps icon size to a proportional typography variant for the name label. */
const typographyBySize: Record<IconSize, TypographyVariant> = {
  xs: 'small2',
  sm: 'body2',
  md: 'body2',
  lg: 'large2',
};

/** Eagerly resolved map of all channel icon SVGs keyed by path. */
const channelIcons: Record<string, { default: string }> = import.meta.glob(
  '@/assets/icons/channels/*/*.svg',
  { eager: true },
);

/**
 * Variant styles for the channel icon slot — a fixed-size container that
 * holds the logo image (or letter avatar). Sizing the slot rather than the
 * image directly gives every channel the same physical visual area for a
 * given (variant, size), so logos with different intrinsic aspect ratios
 * (square marks vs wide logotypes) don't visually compete with each other.
 *
 * - small: square slot (1:1) for compact icons
 * - large: ~4:1 slot to accommodate wide brand marks; narrower logos center
 *   within the slot via the inner image's `object-contain`.
 */
const channelIconVariants = cva('flex shrink-0 items-center justify-center', {
  variants: {
    variant: { small: '', large: '' },
    size: { xs: '', sm: '', md: '', lg: '' },
  },
  compoundVariants: [
    { variant: 'small', size: 'xs', className: 'h-4 w-4' },
    { variant: 'small', size: 'sm', className: 'h-5 w-5' },
    { variant: 'small', size: 'md', className: 'h-6 w-6' },
    { variant: 'small', size: 'lg', className: 'h-8 w-8' },
    { variant: 'large', size: 'xs', className: 'h-4 w-16' },
    { variant: 'large', size: 'sm', className: 'h-5 w-20' },
    { variant: 'large', size: 'md', className: 'h-6 w-24' },
    { variant: 'large', size: 'lg', className: 'h-8 w-32' },
  ],
  defaultVariants: {
    variant: 'small',
    size: 'md',
  },
});

/** Image styling inside a slot: contained within the slot's bounds. */
const channelImageClass = 'max-h-full max-w-full object-contain';

/** Height-only classes for the icon when rendered alongside a title label (no fixed width). */
const iconHeightBySize: Record<IconSize, string> = {
  xs: 'h-4',
  sm: 'h-5',
  md: 'h-6',
  lg: 'h-8',
};

/** Returns the bundled icon src for a recognized channel, or null. */
function getBundledIconSrc(iconVariant: 'small' | 'large', slug?: string) {
  if (!slug) return null;

  const iconName = getChannelIconName(slug.toLowerCase());
  if (!iconName) return null;

  return (
    channelIcons[`/src/assets/icons/channels/${iconVariant}/${iconName}.svg`]
      ?.default ?? null
  );
}

/** Builds an icon element (image or letter-avatar) for use inside ChannelNameLabel. */
function buildIconElement({
  iconSrc,
  imgKey,
  altText,
  size,
  isRounded,
  avatarName,
  useFixedSlot,
}: {
  iconSrc: string | null;
  imgKey: string;
  altText: string;
  size: IconSize;
  isRounded: boolean;
  avatarName: string;
  useFixedSlot: boolean;
}): ReactNode {
  if (iconSrc) {
    const slotClass = useFixedSlot
      ? channelIconVariants({ variant: 'small', size })
      : cn('flex shrink-0 items-center', iconHeightBySize[size]);

    return (
      <div className={slotClass}>
        <img
          key={imgKey}
          src={iconSrc}
          alt={altText}
          className={cn(channelImageClass, isRounded && 'rounded-full')}
        />
      </div>
    );
  }

  return <LetterAvatar name={avatarName} size={size} />;
}

interface ChannelIconProps {
  /** Internal slug used to resolve a bundled SVG. Optional for external channels (e.g. Vonq). */
  channelSlug?: string;
  /** Display name; used as alt text, label, and letter-avatar fallback. */
  channelName?: string;
  /**
   * User-facing title shown alongside the icon with truncation and tooltip.
   * When provided, the icon + title label is always rendered regardless of
   * variant. Overrides `channelName` for visible text, letter-avatar initial,
   * and alt text.
   */
  displayTitle?: string;
  /** External square logo URL (e.g. Vonq `logo_square_url`). */
  logoSquareUrl?: string | null;
  /** External rectangle/wide logo URL (e.g. Vonq `logo_rectangle_url`). */
  logoRectangleUrl?: string | null;
  /** small = square icon, large = full logo (or icon + name fallback) */
  variant?: 'small' | 'large';
  /** Nominal logo size. defaults to md. */
  size?: IconSize;
  /**
   * large variant only. When true (default), the fallback for a missing wide
   * logo renders the square icon or letter avatar beside the channel name.
   * Set to false to keep the fallback confined to the rectangular slot with
   * no name label — useful when the card already shows the name elsewhere.
   */
  showNameLabel?: boolean;
  className?: string;
  /** Extra classes applied to the title text (e.g. color overrides). */
  titleClassName?: string;
  /** Whether to round the icon. Defaults to false. */
  isRounded?: boolean;
}

/**
 * Renders a channel icon from a bundled SVG (by slug) or an external logo URL.
 *
 * When `displayTitle` is provided the component always renders icon + title
 * text (with truncation/tooltip), regardless of variant.
 *
 * Without `displayTitle`, the resolution order is:
 * 1. Bundled SVG matching the requested variant
 * 2. External logo URL matching the requested variant
 * 3. Small variant without any logo: letter avatar
 * 4. Large variant without a wide logo: compact icon + name label
 */
export function ChannelIcon({
  channelSlug,
  channelName,
  displayTitle,
  logoSquareUrl,
  logoRectangleUrl,
  variant = 'small',
  size = 'md',
  showNameLabel = true,
  className,
  titleClassName,
  isRounded = false,
}: ChannelIconProps) {
  const bundledLargeSrc = getBundledIconSrc('large', channelSlug);
  const bundledSmallSrc = getBundledIconSrc('small', channelSlug);
  const largeSrc = bundledLargeSrc ?? logoRectangleUrl ?? null;
  const smallSrc = bundledSmallSrc ?? logoSquareUrl ?? null;

  const imgKey = channelName ?? channelSlug ?? 'channel';

  // --- displayTitle: always render icon + title label ---
  if (displayTitle) {
    const iconSrc =
      variant === 'large' ? (largeSrc ?? smallSrc) : (smallSrc ?? largeSrc);

    return (
      <ChannelNameLabel
        compactIcon={buildIconElement({
          iconSrc,
          imgKey,
          altText: displayTitle,
          size,
          isRounded,
          avatarName: channelSlug ?? channelName ?? displayTitle,
          useFixedSlot: false,
        })}
        channelName={displayTitle}
        size={size}
        className={className}
        titleClassName={titleClassName}
      />
    );
  }

  // --- No displayTitle: original resolution ---

  const requestedSrc = variant === 'large' ? largeSrc : smallSrc;
  const altText = channelName ?? 'Channel';
  const fallbackName = channelSlug ?? channelName;

  // 1 & 2. Bundled SVG or external URL match for the requested variant
  if (requestedSrc) {
    return (
      <div className={cn(channelIconVariants({ variant, size }), className)}>
        <img
          key={imgKey}
          src={requestedSrc}
          alt={altText}
          className={cn(channelImageClass, isRounded && 'rounded-full')}
        />
      </div>
    );
  }

  // 3. Small variant fallback: letter avatar
  if (variant === 'small') {
    if (!fallbackName) return null;
    return (
      <LetterAvatar name={fallbackName} size={size} className={className} />
    );
  }

  // 4. Large variant without wide logo.
  if (!showNameLabel) {
    if (!smallSrc && !fallbackName) return null;
    return (
      <div className={cn(channelIconVariants({ variant, size }), className)}>
        {smallSrc ? (
          <img
            key={imgKey}
            src={smallSrc}
            alt={altText}
            className={channelImageClass}
          />
        ) : (
          <LetterAvatar name={fallbackName ?? ''} size={size} />
        )}
      </div>
    );
  }

  if (!channelName) return null;

  return (
    <ChannelNameLabel
      compactIcon={buildIconElement({
        iconSrc: smallSrc,
        imgKey,
        altText: channelName,
        size,
        isRounded: false,
        avatarName: fallbackName ?? channelName ?? '',
        useFixedSlot: true,
      })}
      channelName={channelName}
      size={size}
      className={className}
      titleClassName={titleClassName}
    />
  );
}

/**
 * Renders a compact icon beside a channel name with truncation detection.
 * Shows a tooltip with the full name when the text overflows.
 */
function ChannelNameLabel({
  compactIcon,
  channelName,
  size,
  className,
  titleClassName,
}: {
  compactIcon: ReactNode;
  channelName: string;
  size: IconSize;
  className?: string;
  titleClassName?: string;
}) {
  const [isTruncated, setIsTruncated] = useState(false);

  const textRef = useCallback(
    (el: HTMLSpanElement | null) => {
      if (!el) return;
      setIsTruncated(el.scrollWidth > el.clientWidth);
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [channelName],
  );

  return (
    <Tooltip content={isTruncated ? channelName : undefined}>
      <div className={cn('flex min-w-0 items-center gap-2', className)}>
        {compactIcon}
        <Typography
          as="span"
          ref={textRef}
          variant={typographyBySize[size]}
          className={cn('block truncate', titleClassName)}
        >
          {channelName}
        </Typography>
      </div>
    </Tooltip>
  );
}

ChannelIcon.Skeleton = function ChannelIconSkeleton({
  variant = 'small',
  size = 'md',
  className,
}: ChannelIconProps) {
  return (
    <Skeleton
      className={cn(channelIconVariants({ variant, size }), className)}
    />
  );
};
